import React from 'react';
import useSWR from 'swr';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { PageLayout } from '../../components/PageLayout';
import { formatMoney, toLocalDateFormat } from '../../utils/functions';

const BackButton = styled.button`
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  font-size: 0.85rem;
  border: 1px solid #6c757d;
  border-radius: 4px;
  background-color: #f8f9fa;
  color: #495057;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #e9ecef;
    border-color: #adb5bd;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 1rem;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    align-items: flex-end;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 0.5rem;
  border: 2px dashed #dee2e6;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 1.5rem;
  color: #155724;
  background: #d4edda;
  border-radius: 0.5rem;
  border: 1px solid #c3e6cb;
  margin-bottom: 1rem;
`;

const SummaryContainer = styled.div`
  background: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #e9ecef;
  
  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .label {
      color: #6c757d;
      font-size: 0.9rem;
    }
    
    .amount {
      font-weight: 600;
      color: #28a745;
      font-size: 1rem;
    }
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0.5rem;
    margin-top: 0.5rem;
    border-top: 2px solid #dee2e6;
    font-weight: 700;
    font-size: 1.1rem;
    color: #212529;
  }
`;

const SummaryDivider = styled.hr`
  margin: 0.75rem 0;
  border: none;
  border-top: 1px solid #dee2e6;
`;

const PaymentsList = styled.div`
  max-height: 500px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const PaymentCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    border-color: #86b7fe;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .payment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .date {
      font-weight: 600;
      color: #495057;
      font-size: 0.9rem;
    }

    .method-inline {
      display: none;
    }
    
    .amount {
      font-weight: 700;
      color: #28a745;
      font-size: 1rem;
      text-align: right;
    }
  }
  
  .payment-details {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
    
    .method {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #6c757d;
      font-size: 0.85rem;
    }
    
    .order-info {
      font-size: 0.8rem;
      color: #6c757d;
    }
  }
  
  .customer-info {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: #495057;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    margin-bottom: 0.35rem;
    
    .payment-header {
      .date {
        font-size: 0.85rem;
        display: flex;
        align-items: center;
        gap: 0.3rem;
      }

      .method-inline {
        display: inline;
        font-size: 0.8rem;
        color: #6c757d;
      }
      
      .amount {
        font-size: 1rem;
      }
    }
    
    .payment-details {
      display: none;
    }
    
    .customer-info {
      font-size: 0.8rem;
      margin-top: 0.2rem;
    }
  }
`;

const getPaymentIcon = (method: string) => {
  switch (method?.toUpperCase()) {
    case 'EFECTIVO': return '💰';
    case 'TRANSFERENCIA': return '🏦';
    case 'YAPE': return '💙';
    case 'PLIN': return '💵';
    case 'TARJETA': return '💳';
    default: return '💸';
  }
};

interface PaymentData {
  id: number;
  date: string;
  amount: number;
  method: string;
  status: string;
  orderId?: number;
  sellOrder?: {
    customerName?: string;
  };
  notes?: string;
}

const CollectedPaymentsPage: React.FC = () => {
  const { data: paymentsData, error } = useSWR<PaymentData[]>('/dashboards/vendor-pending-payments');
  const isLoading = !paymentsData && !error;
  const history = useHistory();

  const calculateTotals = () => {
    if (!paymentsData || paymentsData.length === 0) {
      return {
        total: 0,
        efectivo: 0,
        transferencia: 0
      };
    }

    const efectivo = paymentsData
      .filter(p => ['EFECTIVO', 'YAPE', 'PLIN'].includes((p.method || '').toUpperCase()))
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);

    const transferencia = paymentsData
      .filter(p => ['TRANSFERENCIA', 'TARJETA'].includes((p.method || '').toUpperCase()))
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);

    const total = efectivo + transferencia;

    return { total, efectivo, transferencia };
  };

  const { total, efectivo, transferencia } = calculateTotals();

  if (isLoading) {
    return (
      <PageLayout>
        <HeaderContainer>
          <h5 style={{ margin: 0, textAlign: 'left' }}>Cobros realizados por confirmar</h5>
          <BackButton onClick={() => history.push('/')}>
            ← Regresar
          </BackButton>
        </HeaderContainer>
        <LoadingMessage>Cargando...</LoadingMessage>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <HeaderContainer>
          <h5 style={{ margin: 0, textAlign: 'left' }}>Cobros realizados por confirmar</h5>
          <BackButton onClick={() => history.push('/')}>
            ← Regresar
          </BackButton>
        </HeaderContainer>
        <EmptyMessage>Error al cargar los cobros</EmptyMessage>
      </PageLayout>
    );
  }

  const noPendingConfirmations = (paymentsData?.length ?? 0) === 0;

  return (
    <PageLayout>
      <HeaderContainer>
        <h5 style={{ margin: 0, textAlign: 'left' }}>💰 Cobros Realizados por confirmar</h5>
        <BackButton onClick={() => history.push('/')}>
          ← Regresar
        </BackButton>
      </HeaderContainer>

      {noPendingConfirmations && (
        <SuccessMessage>
          ✓ No hay cobros pendientes de confirmación. Todos los cobros han sido confirmados.
        </SuccessMessage>
      )}

      {(paymentsData?.length ?? 0) > 0 && (
        <SummaryContainer>
          <div className="summary-row">
            <span className="label">💰 Efectivo:</span>
            <span className="amount">{formatMoney(efectivo)}</span>
          </div>
          <div className="summary-row">
            <span className="label">🏦 Transferencia:</span>
            <span className="amount">{formatMoney(transferencia)}</span>
          </div>
          <SummaryDivider />
          <div className="total-row">
            <span>Total Cobrado:</span>
            <span>{formatMoney(total)}</span>
          </div>
          <div className="summary-row" style={{ marginTop: '0.5rem' }}>
            <span className="label">Cantidad de cobros:</span>
            <span className="amount">{paymentsData?.length || 0}</span>
          </div>
        </SummaryContainer>
      )}

      {!paymentsData || paymentsData.length === 0 ? (
        null
      ) : (
        <PaymentsList>
          {paymentsData.map((payment, index) => (
            <PaymentCard key={index}>
              <div className="payment-header">
                <div className="date">
                  {toLocalDateFormat(payment.date)}
                  <span className="method-inline">
                    {getPaymentIcon(payment.method)} {payment.method?.toUpperCase() || ''}
                  </span>
                </div>
                <div className="amount">
                  {formatMoney(Number(payment.amount))}
                </div>
              </div>

              <div className="payment-details">
                <div className="method">
                  <span className="icon">{getPaymentIcon(payment.method)}</span>
                  <span>{payment.method?.toUpperCase() || 'SIN MÉTODO'}</span>
                </div>
                {payment.orderId && (
                  <div className="order-info">
                    Orden: #{payment.orderId}
                  </div>
                )}
              </div>

              {payment.sellOrder?.customerName && (
                <div className="customer-info">
                  👤 {payment.sellOrder.customerName}
                </div>
              )}

              {payment.notes && (
                <div className="customer-info" style={{ fontStyle: 'italic' }}>
                  📝 {payment.notes}
                </div>
              )}
            </PaymentCard>
          ))}
        </PaymentsList>
      )}
    </PageLayout>
  );
};

export default CollectedPaymentsPage;
